import { AxisScale } from '@visx/axis';
import { getTicks } from '@visx/scale';
import {
  type ScaleBand,
  type ScaleLinear,
  type ScaleLogarithmic,
  type ScaleSymLog,
  type ScaleTime,
} from 'd3-scale';

import { ConvertibleUnit, FormatOptions, Unit } from '@dynatrace-sdk/units';

import { defaultUnitFormatter } from './defaultUnitFormatter.js';
import { getTickValues } from './format-value.js';
import { getTickFormatterMethod } from './get-tick-formatter-method.js';
import { getYAxisTickFormatter } from '../components/axes/utils/tick-formatters.js';
import { ChartUnit } from '../types/chart-unit.js';
import {
  DateTimeFormatter,
  Formatter,
  FormatterWithPrecision,
} from '../types/formatter.js';

export interface TickAxis {
  ticks: string[];
  formattedTicks: string[];
}

export function getFormatterByTicks(
  numTicks: number,
  scale:
    | ScaleTime<number, number>
    | ScaleLinear<number, number>
    | ScaleLogarithmic<number, number>
    | ScaleSymLog<number, number>
    | ScaleBand<string>
    | undefined,
  unit?: ChartUnit,
  formatter?: Formatter | FormatOptions<Unit, ConvertibleUnit> | undefined,
) {
  if (!scale) {
    return defaultUnitFormatter(unit);
  }

  const ticks = getTicks(scale as AxisScale, numTicks);

  return getTickFormatterMethod(ticks, formatter, unit);
}

export function getFormattedTicks(
  numTicks: number,
  scale:
    | ScaleLogarithmic<number, number>
    | ScaleSymLog<number, number>
    | ScaleLinear<number, number>
    | ScaleTime<number, number>
    | undefined,
  unit?: ChartUnit,
  formatter?: Formatter | undefined,
) {
  if (!scale) {
    return { ticks: [], formattedTicks: [] };
  }

  const ticks = getTicks(scale as AxisScale, numTicks);

  const formattedTicks = getTickValues(
    ticks,
    getYAxisTickFormatter(scale, unit, formatter, numTicks),
  );

  return { ticks, formattedTicks };
}

export function getDateNumericalFormattedTicks(
  numTicks: number,
  scale: AxisScale | undefined,
  unit: ChartUnit | undefined,
  formatter: FormatterWithPrecision | DateTimeFormatter | undefined,
) {
  if (!scale || !formatter) {
    return { ticks: [], formattedTicks: [] };
  }

  const ticks = getTicks(scale, numTicks);

  const formattedTicks = getTickValues(ticks, (value) =>
    formatter(value as number, unit),
  );

  return { ticks, formattedTicks };
}

export function getCategoricalTicks(scale: AxisScale | undefined) {
  if (!scale) {
    return { ticks: [], formattedTicks: [] };
  }

  return { ticks: scale.domain(), formattedTicks: scale.domain() };
}
